'use client';

import { useState, useCallback, type PropsWithChildren } from 'react';

import type { PanelTypes } from '@/components/panels/panelRegistry';
import { WorkspaceContext } from '@/contexts/workspaceContext';

import {
  PanelSize,
  type SidePanelSize,
  type DialogEntry,
  type DialogOptions,
  type DialogStore,
  type SidePanelStore,
} from './workspaceContextTypes';

export const WorkspaceProvider = ({ children }: PropsWithChildren) => {
  const [dialogStack, setDialogStack] = useState<DialogEntry[]>([]);

  const openDialog = useCallback(
    (id: string, content: React.ReactNode, options?: DialogOptions) => {
      setDialogStack((stack) => [
        ...stack,
        { isLoading: false, ...options, id, content },
      ]);
    },
    []
  );

  const backDialog = useCallback(() => {
    setDialogStack((stack) => stack.slice(0, -1));
  }, []);

  const closeAllDialogs = useCallback(() => {
    setDialogStack([]);
  }, []);

  const replaceDialog = useCallback(
    (id: string, content: React.ReactNode, options?: DialogOptions) => {
      setDialogStack((stack) => [
        ...stack.slice(0, -1),
        { isLoading: false, ...options, id, content },
      ]);
    },
    []
  );

  const isDialogOpen = useCallback(
    (id?: string) => {
      if (id) {
        return dialogStack.some((d) => d.id === id);
      }
      return dialogStack.length > 0;
    },
    [dialogStack]
  );

  const sharedDialog: DialogStore = {
    stack: dialogStack,
    open: openDialog,
    back: backDialog,
    closeAll: closeAllDialogs,
    replace: replaceDialog,
    isOpen: isDialogOpen,
  };

  const [rightPanel, setRightPanel] = useState<SidePanelStore>({
    isOpen: false,
    isAbsolute: false,
    Component: undefined,
    panelSize: PanelSize.Large,
    setSize: (panelSize: SidePanelSize) =>
      setRightPanel((state) => ({ ...state, panelSize })),
    setIsOpen: (
      isOpen: boolean,
      panelSize?: SidePanelSize,
      isAbsolute?: boolean
    ) =>
      setRightPanel((state) => ({
        ...state,
        isOpen,
        isAbsolute: isAbsolute ?? false,
        panelSize: isOpen ? (panelSize ?? PanelSize.Large) : state.panelSize,
      })),
    setComponent: (Component: PanelTypes) =>
      setRightPanel((state) => ({ ...state, Component })),
  });

  const [snackbar, setSnackbar] = useState<{
    isOpen: boolean;
    message: React.ReactNode;
    showMessage: (message: React.ReactNode) => void;
    showErrorMessage: (message: React.ReactNode) => void;
    showWarningMessage: (message: React.ReactNode) => void;
    hide: () => void;
  }>({
    isOpen: false,
    message: '',
    showMessage: (message: React.ReactNode) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        isError: false,
        isWarning: false,
      })),
    showErrorMessage: (message: React.ReactNode) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        isError: true,
        isWarning: false,
      })),
    showWarningMessage: (message: React.ReactNode) =>
      setSnackbar((state) => ({
        ...state,
        isOpen: true,
        message,
        isError: false,
        isWarning: true,
      })),
    hide: () =>
      setSnackbar((state) => ({
        ...state,
        isOpen: false,
        message: '',
        isError: false,
        isWarning: false,
      })),
  });

  return (
    <WorkspaceContext.Provider value={{ rightPanel, snackbar, sharedDialog }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
